<template>
  <transition name="fade">
    <div
      v-if="show"
      :style="modalContainerStyle"
      class="custom_modal"
    >
      <!-- loading div without visible modal -->
      <div
        v-if="loadingWithoutModal"
        ref="loading"
        :style="modalLoadingStyle"
        class="loading-div"
      >
        <div style="display: flex; align-items: center; padding: 10px 20px;">
          <div style="padding-right: 10px;">
            <loader />
          </div>
          <div>{{ loadingText }}</div>
        </div>
        <div
          v-if="progressBar !== 0"
          class="progress"
          style="height: 2px; margin: 0 auto;"
        >
          <div
            :style="'width: ' + progressBar + '%;'"
            aria-valuemax="100"
            aria-valuemin="0"
            aria-valuenow="25"
            class="progress-bar"
            role="progressbar"
          />
        </div>
      </div>
      <!-- end loading div -->

      <!-- modal backdrop -->
      <div
        class="custom_modal__backdrop"
        @click="requestClose"
      />
      <!-- end modal backdrop -->

      <div
        ref="dialog"
        :class="modalClass"
        :style="modalDialogStyle"
        class="custom_modal__dialog"
      >
        <slot name="header-container">
          <div class="custom_modal__header">
            <slot
              v-if="!state.loading"
              name="header"
            />
            <h4 v-else>
&nbsp;
            </h4>
            <button
              class="close"
              type="button"
              :disabled="preventClose"
              @click="requestClose"
            >
              <i class="fas fa-times" />
            </button>
          </div>
        </slot>

        <div class="custom_modal__body">
          <template v-if="state.loading && loadingWithoutModal" />
          <template v-else-if="state.loading && !loadingWithoutModal">
            <div style="margin: 10% auto 15%;">
              <div
                v-if="!skipSpinner"
                class="loader"
              />
              <h4
                v-if="loadingText !== ''"
                style="font-size: 22px; text-align: center;"
              >
                {{ loadingText }}
              </h4>
              <div
                v-if="progressBar !== 0"
                class="progress"
                style="height: 2px; width: 50vh; margin: 0 auto;"
              >
                <div
                  :style="'width: ' + progressBar + '%;'"
                  aria-valuemax="100"
                  aria-valuemin="0"
                  aria-valuenow="25"
                  class="progress-bar"
                  role="progressbar"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <slot name="body" />
          </template>
        </div>

        <div class="custom_modal__footer">
          <slot
            v-if="!state.loading"
            name="footer"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import axios from 'axios'
import Loader from './Loader.vue'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'BaseModal',
  components: { Loader },
  emits: ['on-remote-load', 'on-remote-fail', 'on-close', 'on-open'],
  data() {
    return {
      show: false,
      zindex: 10000,
      state: {
        loading: false
      },
      openedAt: 0
    }
  },
  props: {
    dialogStyle: {
      type: String,
      default: ''
    },
    centerTop: {
      type: Boolean,
      default: false
    },
    ease: {
      type: Boolean,
      default: false
    },
    skipSpinner: {
      type: Boolean,
      default: false
    },
    progressBar: {
      type: Number,
      default: 0
    },
    loadingText: {
      type: String,
      default: ''
    },
    remote: {
      type: Boolean,
      default: false
    },
    remoteLoad: {
      type: String,
      default: ''
    },
    remoteMethod: {
      type: String,
      default: 'GET'
    },
    size: {
      type: String,
      default: 'md'
    },
    nested: {
      type: Boolean,
      default: false
    },
    loadingWithoutModal: {
      type: Boolean,
      default: false
    },
    preventClose: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    loadingState() {
      return this.state.loading
    },
    modalLoadingStyle() {
      return (this.state.loading) ? '' : 'display: none'
    },
    modalDialogStyle() {
      let style = ''
      if (this.dialogStyle !== '') {
        style += `${ this.dialogStyle };`
      }
      return style
    },
    modalClass() {
      const styles = ['modal__size_' + this.$props.size]
      if (this.ease) {
        styles.push('ease')
      }
      if (this.loadingWithoutModal && this.state.loading) {
        styles.push('loading-opacity')
      }
      return styles.join(' ')
    },
    modalContainerStyle() {
      const styles = [`z-index: ${ this.zindex }`]
      if (this.centerTop) {
        styles.push(`display: flex`)
        styles.push(`justify-content: center`)
        styles.push(`align-items: center`)
      }
      return styles.join(';')
    }
  },
  watch: {
    show() {
      if (this.$props.remote === true) {
        this.state.loading = true
      }
    }
  },
  methods: {
    requestClose() {
      if (!this.preventClose) this.closeModal()
    },
    /**
     * Load remote data (if set)
     */
    loadRemote(stamp) {
      const self: any = this

      if (this.$props.remote === true) {
        (axios as any)({
          method: this.$props.remoteMethod,
          url: this.$props.remoteLoad
        }).then(response => {
          self.$emit('on-remote-load', response, stamp)
        }).catch(() => {
          self.$toast.error(self.$options.filters.trans('error.common'))
          self.$emit('on-remote-fail')
          this.closeModal()
        })
      }
    },
    /**
     * UI Ready - method fires from parent.
     */
    uiReady() {
      this.state.loading = false
    },
    uiLoading() {
      this.state.loading = true
    },
    /**
     * Close modal
     */
    closeModal() {
      this.openedAt = 0
      this.show = false
      if (!this.$props.nested) {
        const qs: any = document.querySelector('body')
        qs.classList.remove('overflow-hidden')
      }
      this.$emit('on-close')
    },
    /**
     * Open modal
     */
    openModal() {
      this.openedAt = Date.now()
      this.calculateIndex()
      this.show = true

      const qs: any = document.querySelector('body')
      qs.classList.add('overflow-hidden')
      this.$emit('on-open')

      // get data
      this.loadRemote(this.openedAt)
    },
    calculateIndex() {
      const count = document.querySelectorAll('.modal')
      this.zindex = 10000 + (count.length * 10)
    }
  }
})
</script>

<style lang="scss">
@import "~spinthatshit/src/loaders";

.loader {
  @include loader02;
  border-radius: 100px !important;
  margin: 0 auto;
}

button.close {
  background: none;
  border: none;
}
</style>
